import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Download, Share } from "lucide-react";
import { LogoIcon } from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

const DISMISS_KEY = "buildflow.install.dismissed";
const ACTION_KEY = "buildflow.install.action";
const DISMISS_DAYS = 30; // server-side snooze too (POST /auth/install-state)

export const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
export const isInstalled = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone === true;

// PWA install banner: appears only after the first useful action (photo upload),
// dismissal snoozes 7 days, and it sits below camera/sheet overlays so it never
// blocks forms or capture.
export default function InstallPrompt() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [deferred, setDeferred] = useState(null);
  const deferredRef = useRef(null);
  const [show, setShow] = useState(false);

  // Installed on ANY device (server flag) -> the banner never shows anywhere.
  const serverInstalled = !!user?.app_installed;
  const dismissedTs = Date.parse(user?.install_dismissed_at || "") || 0;
  const serverSnoozed = dismissedTs && Date.now() - dismissedTs < 30 * 864e5;

  useEffect(() => {
    if (isInstalled() || serverInstalled) return;
    const onManual = () => setShow(true); // explicit user action always works
    const onInstalled = () => { setShow(false); setDeferred(null); api.post("/auth/install-state", { installed: true }).catch(() => {}); };
    window.addEventListener("bf:show-install", onManual);
    window.addEventListener("appinstalled", onInstalled);

    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    const snoozed = (dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 864e5) || serverSnoozed;
    const onAction = () => {
      try { localStorage.setItem(ACTION_KEY, "1"); } catch (e) { /* ignore */ }
      if (!snoozed && (isIos() || deferredRef.current)) setShow(true);
    };
    const showIfActionDone = () => {
      try { if (!snoozed && localStorage.getItem(ACTION_KEY)) setShow(true); } catch (e) { /* ignore */ }
    };

    if (isIos()) {
      window.addEventListener("bf:photo-uploaded", onAction);
      showIfActionDone();
      return () => {
        window.removeEventListener("bf:photo-uploaded", onAction);
        window.removeEventListener("bf:show-install", onManual);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }
    const onPrompt = (e) => {
      e.preventDefault();
      deferredRef.current = e;
      setDeferred(e);
      showIfActionDone();
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("bf:photo-uploaded", onAction);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("bf:photo-uploaded", onAction);
      window.removeEventListener("bf:show-install", onManual);
    };
  }, [serverInstalled, serverSnoozed]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
    api.post("/auth/install-state", { dismissed: true }).catch(() => {}); // 30-day server snooze
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (e) { /* ignore */ }
    deferredRef.current = null;
    setDeferred(null);
    setShow(false);
  };

  if (!show || serverInstalled) return null;

  return (
    <div
      data-testid="install-banner"
      className="fixed inset-x-4 bottom-[calc(76px+env(safe-area-inset-bottom,0px))] z-40 md:inset-x-auto md:right-6 md:bottom-6 md:w-96 rounded-2xl border border-brand/40 bg-slate-900/95 backdrop-blur p-4 shadow-2xl"
    >
      <button
        onClick={dismiss}
        data-testid="install-dismiss"
        aria-label={t("install_dismiss")}
        className="absolute right-3 top-3 rounded-full p-1 text-slate-400 hover:text-white"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <LogoIcon size={44} className="shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-bold text-white">{t("install_title")}</div>
          <p className="mt-0.5 text-xs leading-relaxed text-slate-400">
            {isIos() ? t("install_ios_body") : t("install_body")}
          </p>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        {!isIos() && (
          <button
            onClick={install}
            data-testid="install-btn"
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white active:scale-95"
          >
            <Download className="h-4 w-4" /> {t("install_action")}
          </button>
        )}
        {isIos() && (
          <div data-testid="install-ios-hint" className="inline-flex items-center gap-2 rounded-full bg-slate-800 px-4 py-2 text-xs font-semibold text-slate-200">
            <Share className="h-4 w-4" /> {t("install_action")}
          </div>
        )}
        <button
          onClick={dismiss}
          data-testid="install-not-now"
          className="rounded-full px-4 py-2.5 text-xs font-semibold text-slate-400 hover:text-white"
        >
          {t("install_dismiss")}
        </button>
      </div>
    </div>
  );
}
